import React from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import { KEY_DEALS_GROUP_SHELL } from "@FillInTableContainer/shared/constants/dealsKey.constants";
import {
  GROUP_SHELL_PATH_TO_ADD_GROUP_SHELL,
  BASIC_DATA_FOR_ADDING_GROUP_SHELLS,
} from "@FillInTableContainer/shared/constants/tableCreatorGroupShell.constants";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useUpdateEntryData } from "@hooks/useUpdateArticleData";
import { useEntryDataState } from "@stores/entryData";
import { useInitEntryDataValue } from "@stores/initEntryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clickAddRightScopeGroupShellButton: (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => void;
  handleClickToDeleteLicenseeTableGroupShell: () => void;
  handleKeyDownToDeleteLicenseeTableGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDealLaScRightsScopeGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const initEntryData = useInitEntryDataValue(getPressIdFromURL());
  const updateEntryData = useUpdateEntryData();
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteGroupShell } = useCheckDisabledDelete();

  const handleAddGroupShell = (groupShellInfo: {
    tableIndexParameter: number;
    groupShellIndexParameter: number;
    groupShellKeyParameter: string | null;
  }) => {
    if (focusStatus.container !== "noFocus") return;

    const {
      tableIndexParameter,
      groupShellIndexParameter,
      groupShellKeyParameter,
    } = groupShellInfo;

    if (groupShellKeyParameter === null) return;

    const targetShell = GROUP_SHELL_PATH_TO_ADD_GROUP_SHELL[
      groupShellKeyParameter
    ](entryData, tableIndexParameter, groupShellIndexParameter);

    if (targetShell === undefined) return;

    const newArrayLength = targetShell.length + 1 || 1;
    const emptyArray = new Array(newArrayLength).fill({});

    const addRightsScopeGroupShellData = emptyArray.map((_, index) => {
      const RightScopeGroupShellData =
        BASIC_DATA_FOR_ADDING_GROUP_SHELLS[articleSection][
          groupShellKeyParameter
        ](initEntryData);

      if (groupShellKeyParameter !== KEY_DEALS_GROUP_SHELL.rightsScope) return;

      if (index === groupShellIndexParameter) {
        return RightScopeGroupShellData;
      }

      if (index > groupShellIndexParameter) {
        return targetShell[index - 1];
      }

      if (index < groupShellIndexParameter) {
        return targetShell[index];
      }

      return targetShell[index];
    });

    const changeGroupShellData: Record<string, ArticleEntryData> = {
      "Rights Scope Row": {
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Rights Scope Table": addRightsScopeGroupShellData,
        },
      } as ArticleEntryData,
    };

    setEntryData(changeGroupShellData[groupShellKeyParameter]);
    updateEntryData(changeGroupShellData[groupShellKeyParameter]);
    changeCurrentHistory({
      entryData: changeGroupShellData[groupShellKeyParameter],
      changedDataInfo: {
        target: "groupShell",
        action: "add",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex: groupShellIndexParameter,
          subGroupShellIndex: null,
          subGroupShellKey: null,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
  };

  const clickAddRightScopeGroupShellButton = (
    event: React.MouseEvent<HTMLButtonElement>,
    groupShellInfo: {
      tableIndexParameter: number;
      groupShellIndexParameter: number;
      groupShellKeyParameter: string | null;
    },
  ) => {
    event.stopPropagation();
    handleAddGroupShell(groupShellInfo);
  };

  const handleClearGroupShell = () => {
    const clearRightScopeGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Rights Scope Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Rights Scope Table"
          ]?.map((groupShellInfo) => {
            return {
              ...groupShellInfo,
              Asset: {
                ...groupShellInfo.Asset,
                value: [],
              },
              "Rights Scope Type": {
                ...groupShellInfo["Rights Scope Type"],
                value: [],
              },
              "Rights Scope": {
                ...groupShellInfo["Rights Scope"],
                value: [],
              },
              "Rights Flow": {
                "Axis Direction": {
                  ...groupShellInfo?.["Rights Flow"]?.["Axis Direction"],
                  value: [],
                },
                "Benefiting Axis": {
                  ...groupShellInfo?.["Rights Flow"]?.["Benefiting Axis"],
                  value: [],
                },
                "Contributing Axis": {
                  ...groupShellInfo?.["Rights Flow"]?.["Contributing Axis"],
                  value: [],
                },
              },
              "Rights Limitation": {
                ...groupShellInfo?.["Rights Limitation"],
                value: [],
              },
              "Intended Use": {
                ...groupShellInfo?.["Intended Use"],
                value: [],
              },
              Exclusivity: {
                ...groupShellInfo?.Exclusivity,
                value: [],
              },
              Territory: {
                ...groupShellInfo?.Territory,
                value: [],
              },
              "Territory Exclude": {
                ...groupShellInfo?.["Territory Exclude"],
                value: [],
              },
              "Territory Include": {
                ...groupShellInfo?.["Territory Include"],
                value: [],
              },
            };
          }),
        },
      };
    };

    if (info.groupShellKey === "Rights Scope Row") {
      setEntryData(clearRightScopeGroupShell());
      changeCurrentHistory({
        entryData: clearRightScopeGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            subGroupShellIndex: null,
            subGroupShellKey: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleDeleteGroupShell = () => {
    if (checkDisabledDeleteGroupShell()) return;

    const deleteRightScopeGroupShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Rights Scope Table": entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Rights Scope Table"
          ]?.map((groupShellInfo, groupShellInfoIndex) => {
            if (groupShellInfoIndex === info.groupShellIndex) {
              return {
                ...groupShellInfo,
                Asset: {
                  ...groupShellInfo.Asset,
                  value: [],
                },
                "Rights Scope Type": {
                  ...groupShellInfo["Rights Scope Type"],
                  value: [],
                },
                "Rights Scope": {
                  ...groupShellInfo["Rights Scope"],
                  value: [],
                },
                "Rights Flow": {
                  "Axis Direction": {
                    ...groupShellInfo?.["Rights Flow"]?.["Axis Direction"],
                    value: [],
                  },
                  "Benefiting Axis": {
                    ...groupShellInfo?.["Rights Flow"]?.["Benefiting Axis"],
                    value: [],
                  },
                  "Contributing Axis": {
                    ...groupShellInfo?.["Rights Flow"]?.["Contributing Axis"],
                    value: [],
                  },
                },
                "Rights Limitation": {
                  ...groupShellInfo?.["Rights Limitation"],
                  value: [],
                },
                "Intended Use": {
                  ...groupShellInfo?.["Intended Use"],
                  value: [],
                },
                Exclusivity: {
                  ...groupShellInfo?.Exclusivity,
                  value: [],
                },
                Territory: {
                  ...groupShellInfo?.Territory,
                  value: [],
                },
                "Territory Exclude": {
                  ...groupShellInfo?.["Territory Exclude"],
                  value: [],
                },
                "Territory Include": {
                  ...groupShellInfo?.["Territory Include"],
                  value: [],
                },
              };
            }

            return groupShellInfo;
          }),
        },
      };
    };
    setEntryData(deleteRightScopeGroupShell());
    changeCurrentHistory({
      entryData: deleteRightScopeGroupShell(),
      changedDataInfo: {
        target: "groupShell",
        action: "delete",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex: null,
          subGroupShellKey: null,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });

    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const handleClickToDeleteLicenseeTableGroupShell = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShell();
    }
    if (focusStatus.type === "delete") {
      handleDeleteGroupShell();
    }
  };

  const handleKeyDownToDeleteLicenseeTableGroupShell = (
    event: KeyboardEvent,
  ) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearGroupShell();
    }
    if (focusStatus.type === "delete" && event.key === "Delete") {
      handleDeleteGroupShell();
    }
  };

  return {
    clickAddRightScopeGroupShellButton,
    handleClickToDeleteLicenseeTableGroupShell,
    handleKeyDownToDeleteLicenseeTableGroupShell,
  };
};
